function waitForIceGathering(pc, timeoutMs = 800) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', onChange)
      resolve()
    }, timeoutMs)
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(timer)
        pc.removeEventListener('icegatheringstatechange', onChange)
        resolve()
      }
    }
    pc.addEventListener('icegatheringstatechange', onChange)
  })
}

function waitForPlayback(videoEl, pc, timeoutMs = 3000) {
  return new Promise((resolve, reject) => {
    const done = (fn) => {
      clearTimeout(timer)
      videoEl.removeEventListener('loadeddata', onVideo)
      videoEl.removeEventListener('playing', onVideo)
      pc.removeEventListener('connectionstatechange', onConn)
      fn()
    }
    const onVideo = () => {
      if (videoEl.videoWidth > 0) done(resolve)
    }
    const onConn = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        done(() => reject(new Error(`WHEP ${pc.connectionState}`)))
      }
    }
    const timer = setTimeout(() => {
      done(() => reject(new Error('WHEP playback timeout')))
    }, timeoutMs)

    videoEl.addEventListener('loadeddata', onVideo)
    videoEl.addEventListener('playing', onVideo)
    pc.addEventListener('connectionstatechange', onConn)
    onVideo()
  })
}

/**
 * @returns {{ pc: RTCPeerConnection, resourceUrl: string | null, whenReady: Promise<void> }}
 */
export async function connectWhep(videoEl, whepUrl) {
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  })

  pc.addTransceiver('video', { direction: 'recvonly' })
  pc.addTransceiver('audio', { direction: 'recvonly' })

  pc.ontrack = (event) => {
    const [stream] = event.streams
    videoEl.srcObject = stream ?? new MediaStream([event.track])
  }

  try {
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    await waitForIceGathering(pc)

    const response = await fetch(whepUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription?.sdp ?? '',
      credentials: 'omit',
    })

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(text || `WHEP ${response.status}`)
    }

    const answerSdp = await response.text()
    const location = response.headers.get('Location')
    const resourceUrl = location ? new URL(location, whepUrl).toString() : null
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })

    try {
      await videoEl.play()
    } catch {
      // muted autoplay usually ok
    }

    // Do not block return on first frame — UI can clear spinner via video events.
    const whenReady = waitForPlayback(videoEl, pc)
    return { pc, resourceUrl, whenReady }
  } catch (err) {
    pc.close()
    videoEl.srcObject = null
    throw err
  }
}

export async function disconnectWhep(pc, resourceUrl) {
  if (resourceUrl) {
    try {
      await fetch(resourceUrl, { method: 'DELETE', credentials: 'omit' })
    } catch {
      // ignore
    }
  }
  pc?.close()
}
